import { Action, Priority, useRegisterActions } from "kbar";
import { useCallback, useMemo } from "react";

import { SyncListItemFragment, useListSyncsLazyQuery } from "@/apollo/types";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { formatModelName } from "@/features/models";
import cronExpressionToDayjs from "@/helpers/cronExpressionToDayjs";
import { getCronInfo } from "@/hooks/useCron";
import { IntegrationLogo, useIntegrationsMap } from "@/integrations";
import { useNavigateWithSlug } from "@/router";
import { formatRelativeTime } from "@/shared/formatters";

const formatSource = (sync: SyncListItemFragment) => {
  if (sync.rawView) {
    return `${sync.rawView.schema}.${sync.rawView.sourceStream}`;
  }
  if (sync.model) {
    return formatModelName(sync.model);
  }
  return "Unknown Source";
};

const formatName = (sync: SyncListItemFragment) => {
  const connectionLabel =
    sync.destinationConnection.label || sync.destinationIntegrationId;

  if (sync.destinationIntegrationId.includes("google-sheets")) {
    return `${sync.config?.destinationSettings?.spreadsheet?.label} → ${sync.config?.destinationSettings?.sheet?.label} (${formatSource(sync)})`;
  }

  if (sync.destinationIntegrationId.includes("google-drive")) {
    return `${sync.config?.destinationSettings?.folder?.label} → ${sync.config?.destinationSettings?.fileType?.label} (${formatSource(sync)})`;
  }

  if (sync.destinationIntegrationId.includes("onedrive")) {
    return `onedrive → ${sync.config?.destinationSettings?.fileType?.label} (${formatSource(sync)})`;
  }

  return `${sync.primaryObject} - ${connectionLabel} (${formatSource(sync)})`;
};

const formatSchedule = (sync: SyncListItemFragment) => {
  const cronExpr =
    (sync.orchestrationWorkflow
      ? sync.orchestrationWorkflow.cronExpression
      : sync.syncInterval) || undefined;

  if (!cronExpr) {
    return "No schedule set";
  }

  const cronInfo = getCronInfo(cronExpr).info;
  return `${cronInfo} - ${formatRelativeTime(cronExpressionToDayjs(cronExpr).toDate())}`;
};

export function useReverseEtlSyncActions() {
  const [fetchSyncs, { data, loading }] = useListSyncsLazyQuery();

  const navigate = useNavigateWithSlug();

  const integrationsMap = useIntegrationsMap();

  const actions: Action[] = useMemo(() => {
    const actions: Action[] = [];

    if (loading) {
      actions.push({
        id: "loading-reverse-etl-syncs",
        name: "Loading ...",
        priority: Priority.HIGH,
        section: "Reverse ETL Syncs",
        icon: <LoadingSpinner id="loading" className="size-4" />,
        perform: () => {},
      });
    }

    data?.syncs.forEach((sync) => {
      actions.push({
        id: sync.id,
        name: formatName(sync),
        subtitle: formatSchedule(sync),
        keywords: [
          sync.destinationIntegrationId,
          integrationsMap
            .get(sync.destinationIntegrationId)
            ?.name.toLowerCase(),
        ]
          .filter(Boolean)
          .join(" "),
        priority: Priority.NORMAL,
        section: "Reverse ETL Syncs",
        icon: (
          <IntegrationLogo
            id={sync.destinationIntegrationId}
            className="size-4"
          />
        ),
        perform: () => {
          navigate({
            to: `/reverse-etl/${sync.id}`,
          });
        },
      });
    });

    return actions;
  }, [data, loading, integrationsMap, navigate]);

  useRegisterActions(actions, [data?.syncs, loading]);

  const register = useCallback(async () => {
    await fetchSyncs();
  }, [fetchSyncs]);

  return register;
}
